/**
 * Chat API Service
 * Handles all chat-related API calls for multi-chat support
 */
import { chatApiInstance } from './api';

/**
 * Get all chats for an account or tenant
 * @param {string} accountId - Account ID (preferred)
 * @param {string} tenantId - Tenant ID (backward compatibility, used when accountId not provided)
 * @returns {Promise} List of chats
 */
export const getChats = async (accountId, tenantId = null) => {
  const params = {};
  if (accountId) {
    params.account_id = accountId;
  } else if (tenantId) {
    params.tenant_id = tenantId;
  }
  return chatApiInstance.get('/', { params });
};

/**
 * Get details of a specific chat
 * @param {string} chatId - Chat ID
 * @param {string} accountId - Account ID
 * @returns {Promise} Chat details
 */
export const getChat = async (chatId, accountId) => {
  return chatApiInstance.get(`/${chatId}`, {
    params: { account_id: accountId }
  });
};

/**
 * Create a new chat
 * @param {Object} chatData - Chat data (name, chatwoot_inbox_id, account_id)
 * @returns {Promise} Created chat
 */
export const createChat = async (chatData) => {
  return chatApiInstance.post('/', chatData);
};

/**
 * Update an existing chat
 * @param {string} chatId - Chat ID
 * @param {string} accountId - Account ID
 * @param {Object} updateData - Fields to update
 * @returns {Promise} Updated chat
 */
export const updateChat = async (chatId, accountId, updateData) => {
  return chatApiInstance.put(`/${chatId}`, updateData, {
    params: { account_id: accountId }
  });
};

/**
 * Rotate a chat's widget_token (super admin only).
 * Returns the axios response; caller should read `.data.widget_token`.
 * @param {string} chatId - Chat ID
 * @param {string} accountId - Account ID
 * @returns {Promise} Axios response with { chat_id, widget_token } in .data
 */
export const rotateWidgetToken = async (chatId, accountId) => {
  return chatApiInstance.post(`/${chatId}/widget-token/rotate`, null, {
    params: { account_id: accountId },
  });
};

/**
 * Deactivate a chat (soft delete)
 * @param {string} chatId - Chat ID
 * @param {string} accountId - Account ID
 * @returns {Promise} Success message
 */
export const deactivateChat = async (chatId, accountId) => {
  return chatApiInstance.patch(`/${chatId}/deactivate`, null, {
    params: { account_id: accountId },
  });
};

/**
 * Activate a previously deactivated chat
 * @param {string} chatId - Chat ID
 * @param {string} accountId - Account ID
 * @returns {Promise} Success message
 */
export const activateChat = async (chatId, accountId) => {
  return chatApiInstance.patch(`/${chatId}/activate`, null, {
    params: { account_id: accountId },
  });
};

/**
 * Permanently delete a chat (only works for inactive chats)
 * @param {string} chatId - Chat ID
 * @param {string} accountId - Account ID
 * @returns {Promise} Success message
 */
export const deleteChatPermanently = async (chatId, accountId) => {
  return chatApiInstance.delete(`/${chatId}`, {
    params: { account_id: accountId },
  });
};

// Alias for backward compatibility
export const deleteChat = deactivateChat;

/**
 * Get the identity-cookie integration configured for a chat (super admin only).
 * Mirrors a first-party cookie (cookieName) into a contact attribute
 * (attributeKey) for this chat; the CDP fields (Bloomreach Engagement) are an
 * optional extension on top of that. The route path keeps its original
 * `/bloomreach` segment on purpose — only the underlying field/column was
 * renamed (`bloomreach_integration` → `identity_cookie_integration`), not
 * this URL.
 * @param {string} chatId - Chat ID
 * @returns {Promise} Axios response with { integration: {…}|null } in .data
 */
export const getIdentityCookieIntegration = async (chatId) => {
  return chatApiInstance.get(`/${chatId}/bloomreach`);
};

/**
 * Create/update/disable the identity-cookie integration for a chat (super
 * admin only). Pass `integration: null` to disable it.
 * @param {string} chatId - Chat ID
 * @param {Object|null} integration - Identity-cookie integration object, or null to disable
 * @returns {Promise} Axios response with { integration: {…}|null } in .data
 */
export const updateIdentityCookieIntegration = async (chatId, integration) => {
  return chatApiInstance.put(`/${chatId}/bloomreach`, { integration });
};

/**
 * The chat's state for the Chatwoot handoff (super admin only, RPAA-201 phase 5).
 * Reads Chatwoot and changes nothing. A Chatwoot that does not answer is the state
 * chatwoot_error, not an HTTP error.
 * @param {string} chatId - Chat ID
 * @returns {Promise} Axios response with { state, problems, chatwoot_version, inbox, message } in .data
 */
export const getHandoffSetup = async (chatId) => {
  return chatApiInstance.get(`/${chatId}/handoff-setup`);
};

/**
 * Set the chat up for the Chatwoot handoff (super admin only): creates the chat's
 * transfer inbox in Chatwoot and records it on the chat. 409 unless the state is
 * not_configured. A failure part-way is a 200 whose `failure` says what was left.
 * @param {string} chatId - Chat ID
 * @returns {Promise} Axios response with { steps, failure, status } in .data
 */
export const setUpHandoff = async (chatId) => {
  return chatApiInstance.post(`/${chatId}/handoff-setup`);
};

/**
 * Add a file to a chat's access list
 * @param {string} filePath - File path
 * @param {string} chatId - Chat ID
 * @param {string} collectionName - Collection name
 * @returns {Promise} Success message
 */
export const addFileToChat = async (filePath, chatId, collectionName) => {
  const formData = new FormData();
  formData.append('file_path', filePath);
  formData.append('chat_id', chatId);
  formData.append('collection_name', collectionName);
  
  // Note: These file operations use different base path (files API)
  const { createApiInstance } = await import('./api');
  const fileApiInstance = createApiInstance('/files');
  return fileApiInstance.post('/add-to-chat', formData);
};

/**
 * Remove a file from a chat's access list
 * @param {string} filePath - File path
 * @param {string} chatId - Chat ID
 * @param {string} collectionName - Collection name
 * @returns {Promise} Success message
 */
export const removeFileFromChat = async (filePath, chatId, collectionName) => {
  const formData = new FormData();
  formData.append('file_path', filePath);
  formData.append('chat_id', chatId);
  formData.append('collection_name', collectionName);
  
  const { createApiInstance } = await import('./api');
  const fileApiInstance = createApiInstance('/files');
  return fileApiInstance.post('/remove-from-chat', formData);
};

/**
 * Invalidate all cache for a specific chat
 * @param {string} chatId - Chat ID
 * @returns {Promise} Success message
 */
export const invalidateChatCache = async (chatId) => {
  const { createApiInstance } = await import('./api');
  const cacheApiInstance = createApiInstance('/cache');
  return cacheApiInstance.post(`/invalidate-chat/${chatId}`);
};
